import {
  useState, useContext, useEffect,
  useCallback, useRef, memo
} from 'react';
import { createPortal } from 'react-dom';
import UserIcon from './icons/user.svg?react';
import SettingsGearFillIcon from './icons/settingsgearfill.svg?react';
import LogoutIcon from './icons/logout.svg?react';
import AnalyticsIcon from './icons/analytics.svg?react';
import ChevronDownIcon from './icons/chevrondown.svg?react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from './AppContext.tsx';

/* ------------------------------------------------------------------ */
/*  tiny hooks                                                         */
/* ------------------------------------------------------------------ */
function useOutsideClick(open, refs, closeFn) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (refs.every(r => r.current && !r.current.contains(e.target))) closeFn();
    };
    document.addEventListener('mousedown', handler, { passive: true });
    return () => document.removeEventListener('mousedown', handler);
  }, [open, closeFn, refs]);
}

function useEscapeToClose(open, setOpen) {
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (open) {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, setOpen]);
}

function useSessionSync(authSession, setDropdownOpen) {
  useEffect(() => {
    if (!authSession) setDropdownOpen(false);
  }, [authSession, setDropdownOpen]);
}

/* ------------------------------------------------------------------ */
/*  presentation pieces                                                */
/* ------------------------------------------------------------------ */
const AccountButton = ({
  refProp, minimal, authSession, isOpen, onClick
}) => (
  <button
    type="button"
    ref={refProp}
    onClick={onClick}
    aria-expanded={isOpen}
    aria-haspopup="menu"
    aria-label={authSession ? `Account menu for ${authSession.user.email}` : 'Sign in'}
    className={`h-7 relative flex items-center justify-between rounded p-spacing-2 pr-spacing-2 transition-colors duration-200
      hover:bg-bg-hover focus:outline-none focus:bg-bg-hover
      ${minimal ? '' : 'w-full'}
      ${isOpen ? 'bg-bg-hover' : ''}`}
  >
    {authSession ? (
      <>
        <div className="flex items-center gap-2">
          <UserIcon className="size-4" style={{ color: 'var(--color-primary)'}}/>
          <div className="flex flex-col items-start">
            <span className="text-sm text-primary leading-tight truncate max-w-[140px]">
              {authSession.user.email}
            </span>
          </div>
        </div>
        <ChevronDownIcon className={`size-4 transition-transform duration-200 ${
          isOpen ? 'rotate-180' : ''
        }`}/>
      </>
    ) : (
      <div className="flex items-center gap-2 w-full">
        <LogoutIcon className="size-5 text-primary" style={{ color: 'var(--color-primary)'}} />
        <span className="text-sm text-primary">Sign in</span>
      </div>
    )}
  </button>
);

const AuthPopup = memo(({ popupRef, onSettings, onUsage, onLogout, position }) => (
  <div
    ref={popupRef}
    role="menu"
    aria-orientation="vertical"
    aria-label="Account menu"
    className="fixed p-1 border border-border-default rounded-md w-[244px] flex flex-col bg-bg-dropdown shadow-lg z-50"
    style={{
      top: `${position.top}px`,
      left: `${position.left}px`,
    }}
  >
    {/* Menu items */}
    <div className="">
      <button
        role="menuitem"
        className="w-full flex items-center gap-2 h-7 px-1 text-sm text-primary hover:bg-bg-hover transition-colors duration-200 rounded focus:outline-none focus:bg-bg-hover"
        onClick={onSettings}
      >
        <SettingsGearFillIcon className="size-4" aria-hidden="true" />
        <span>Settings</span>
      </button>
      
      <button
        role="menuitem"
        className="w-full flex items-center gap-2 h-7 px-1 text-sm text-primary hover:bg-bg-hover transition-colors duration-200 rounded focus:outline-none focus:bg-bg-hover"
        onClick={onUsage}
      >
        <AnalyticsIcon className="w-4 h-4" aria-hidden="true" />
        <span>Usage</span>
      </button>
      
      <div className="my-1 border-t border-border-divider" role="separator" />
      
      <button
        role="menuitem"
        className="w-full flex items-center gap-2 h-7 px-1 text-sm text-primary hover:bg-bg-hover transition-colors duration-200 rounded focus:outline-none focus:bg-bg-hover"
        onClick={onLogout}
      >
        <LogoutIcon className="size-4" aria-hidden="true" />
        <span>Log out</span>
      </button>
    </div>
  </div>
));

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */
function Authentication({ minimal = false }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const navigate = useNavigate();

  const {
    supabaseClient,
    authSession,
    menuIsOpen
  } = useContext(AppContext);

  /* ---------- refs ---------- */
  const popupRef     = useRef(null);
  const toggleRef    = useRef(null);

  /* ---------- custom hooks ---------- */
  useOutsideClick(dropdownOpen, [popupRef, toggleRef], () => setDropdownOpen(false));
  useEscapeToClose(dropdownOpen, setDropdownOpen);
  useSessionSync(authSession, setDropdownOpen);

  /* ---------- position calculation ---------- */
  useEffect(() => {
    if (dropdownOpen && toggleRef.current) {
      const calculatePosition = () => {
        const btnRect = toggleRef.current.getBoundingClientRect();
        // Measure popup height if it's already rendered, otherwise use estimate
        const popupHeight = popupRef.current?.offsetHeight || 120;
        // Position above the button: button top - popup height - margin
        setPopupPosition({
          top: btnRect.top - popupHeight - 8, // 8px margin (mb-2)
          left: btnRect.left,
        });
      };

      // Initial calculation
      calculatePosition();

      // Recalculate after popup renders to get accurate height
      requestAnimationFrame(() => {
        requestAnimationFrame(calculatePosition);
      });
    }
  }, [dropdownOpen]);

  /* ---------- callbacks ---------- */
  const logout = useCallback(async () => {
    const { error } = await supabaseClient.auth.signOut();
    if (error) {
      console.error('Logout error:', error.message);
    } else {
      setDropdownOpen(false);
    }
  }, [supabaseClient]);

  const handleSettings = useCallback(() => {
    setDropdownOpen(false);
    navigate('/settings');
  }, [navigate]);

  const handleUsage = useCallback(() => {
    setDropdownOpen(false);
    navigate('/usage');
  }, [navigate]);

  const handleAccountButton = useCallback(() => {
    if (authSession) {
      setDropdownOpen(p => !p);
    } else {
      // Redirect to login page
      navigate('/login');
    }
  }, [authSession, navigate]);


  /* ---------- render ---------- */
  return (
    <div
      className="relative opacity-100 mt-auto"
    >
      <div className="w-full">
        <AccountButton
          refProp={toggleRef}
          minimal={minimal}
          authSession={authSession}
          isOpen={dropdownOpen}
          onClick={handleAccountButton}
        />
      </div>

      {dropdownOpen && createPortal(
        <AuthPopup
          popupRef={popupRef}
          onSettings={handleSettings}
          onUsage={handleUsage}
          onLogout={logout}
          position={popupPosition}
        />,
        document.body
      )}
    </div>
  );
}

export default memo(Authentication);
